import type { BrowserOptions } from '@sentry/react';

type DataCollection = NonNullable<BrowserOptions['dataCollection']>;

/**
 * Sentry 11 replaced `sendDefaultPii` with `dataCollection`, and flipped the default: a
 * category that is NOT listed here is COLLECTED (cookies, request and response bodies,
 * headers, GenAI prompts and completions, user info). Every hosting app passes this object,
 * so nothing is left to that default.
 *
 * `satisfies Required<DataCollection>` makes BOTH a misspelled key and a missing key a
 * compile error (every key of the SDK's interface is optional on its own, so a plain
 * `satisfies` would let an omitted category fall back to "collect");
 * `sentryDataCollection.test.ts` pins the key set and that every leaf says "off". Change a
 * value here only with the reason written next to it.
 *
 * The one exception is the request `user-agent` header: Sentry derives the browser and OS
 * tags from it, triage filters on them, and the legacy-browser / crawler inbound filter
 * needs it. Every other header is off, so the Referer (the previous page URL) is replaced by
 * `[Filtered]` before it reaches `scrubEventUrls`, which scrubs it again anyway.
 *
 * This file imports only a TYPE from `@sentry/react`, which is erased at build time, so it
 * adds no bare specifier for the satellites that bundle `client/src` through `@shared`
 * (see buildIdentity.ts for why that matters).
 */
export const SENTRY_DATA_COLLECTION = {
  userInfo: false, // no IP inference; the apps set the user themselves where it is allowed
  cookies: false,
  httpHeaders: { request: { allow: ['user-agent'] }, response: false },
  httpBodies: [], // never a request or response body
  urlQueryParams: false, // the scrubbers strip queries anyway; belt and braces
  graphQL: { document: false, variables: false },
  genAI: { inputs: false, outputs: false },
  mcp: { inputs: false, outputs: false },
  databaseQueryData: false,
  queues: false,
  stackFrameVariables: false, // not captured in a browser bundle; stated so the set is complete
  frameContextLines: 0, // no source context lines; stated for the same reason
} satisfies Required<DataCollection>;
